import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { emailVerificationApi } from '../api/emailVerificationApi';
import { tenantQueryKeys } from './useTenantQuery';
import type { EmailFormData } from '@xtnd/web/shared/types/tenantSettingTypes';

/**
 * Hook to save email configuration (legacy)
 */
export function useEmailConfigMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: EmailFormData) => {
            const currentConfig = await tenantSettingsApi.getCurrentTenantConfig();
            const currentTenantSettings = currentConfig.modules?.tenantSettings || {};

            const apiPayload = {
                modules: {
                    ...currentConfig.modules,
                    tenantSettings: {
                        ...currentTenantSettings,
                        emailNotifications: {
                            operationsSupportEmail: data.operationsSupportEmail,
                            technicalSupportEmail: data.technicalSupportEmail,
                            firmInvoiceFromEmail: data.firmInvoiceFromEmail,
                            firmInvoiceBccEmail: data.firmInvoiceBccEmail,
                        },
                        settingsComponent: 'TenantSettingsModal',
                        healthCheckEndpoint: '/health/tenantSettings',
                    },
                },
            };

            return await tenantSettingsApi.updateCurrentTenantConfig(apiPayload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tenantQueryKeys.config });
            queryClient.invalidateQueries({ queryKey: tenantQueryKeys.configurationStatus });
            showToast.success('Email configuration saved successfully!');
        },
        onError: error => {
            console.error('Email config save error:', error);
            showToast.error('Failed to save email configuration');
        },
    });
}

/**
 * Hook to send email verification
 */
export function useSendEmailVerification() {
    return useMutation({
        mutationFn: async (email: string) => {
            return emailVerificationApi.sendVerificationEmail(email);
        },
        onError: (error: any) => {
            showToast.error(`Failed to send verification email: ${error.message || 'Unknown error'}`);
            console.error('Verification error:', error);
        },
    });
}

/**
 * Hook to check email verification status
 */
export function useCheckEmailVerificationStatus() {
    return useMutation({
        mutationFn: (email: string) => emailVerificationApi.getVerificationStatus(email),
    });
}

/**
 * Hook to verify email with tracking (for Email Config Modal)
 */
export function useVerifyEmailWithTracking() {
    return useMutation({
        mutationFn: ({ email }: { email: string; fieldName?: string }) =>
            emailVerificationApi.verifyEmailWithTracking(email),
        onSuccess: (result) => {
            showToast.success(`Verification email sent to ${result.email}`);
        },
        onError: () => {
            showToast.error('Failed to send verification email');
        },
    });
}

